import { useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const PRESETS = [50, 90, 100, 150, 200];

export function BandwidthCapDialog({ router, onClose }) {
  const [ifaces, setIfaces] = useState([]);
  const [info, setInfo] = useState(null);
  const [mbps, setMbps] = useState("");
  const [wan, setWan] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!router) return;
    setInfo(null); setIfaces([]); setMbps(""); setWan("");
    api.get(`/traffic/${router.id}/interfaces`).then((r) => setIfaces(r.data)).catch(() => {});
    api.get(`/bandwidth-cap/${router.id}`).then((r) => {
      setInfo(r.data);
      if (r.data.saved?.mbps) { setMbps(String(r.data.saved.mbps)); setWan(r.data.saved.wan || ""); }
    }).catch((e) => toast.error(errMsg(e)));
  }, [router]);

  const save = async () => {
    if (!wan) return toast.error("Pilih interface WAN (ke Starlink/ISP)");
    setBusy(true);
    try {
      await api.put(`/bandwidth-cap/${router.id}`, { mbps: Number(mbps), wan });
      toast.success(`Bandwidth ${router.name} dibatasi ${mbps} Mbps`);
      onClose(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await api.delete(`/bandwidth-cap/${router.id}`); toast.success("Batas bandwidth dihapus"); onClose(true); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const live = info?.live;
  return (
    <Dialog open={!!router} onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="dialog-bandwidth-cap">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-amber-400" /> Batas Bandwidth — {router?.name}</DialogTitle>
          <DialogDescription>Total pemakaian seluruh pelanggan di cabang ini dibatasi dari MikroTik (queue tree) agar link Starlink/ISP tetap lancar.</DialogDescription>
        </DialogHeader>
        {live && (
          <div className="rounded-lg border border-border bg-accent/40 px-3 py-2 text-xs" data-testid="cap-live-status">
            {live.error ? <span className="text-rose-400">Router tidak terjangkau: {live.error}</span>
              : live.active ? <span>Aktif di router: <b className="font-mono">{live.max_limit}</b> via <b className="font-mono">{live.wan}</b> — pakai saat ini ↓{live.rate_down} ↑{live.rate_up}</span>
              : <span className="text-muted-foreground">Belum ada batas bandwidth di router ini.</span>}
          </div>
        )}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Bandwidth diberikan (Mbps)</Label>
            <Input type="number" min="1" value={mbps} onChange={(e) => setMbps(e.target.value)} placeholder="contoh 90" data-testid="input-cap-mbps" />
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => <button key={p} type="button" onClick={() => setMbps(String(p))} className={`px-2 py-0.5 rounded-full text-xs border ${String(p) === mbps ? "bg-amber-500 text-black border-amber-500" : "border-border hover:bg-accent"}`} data-testid={`cap-preset-${p}`}>{p} Mbps</button>)}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Interface WAN (yang ke Starlink/ISP)</Label>
            <select value={wan} onChange={(e) => setWan(e.target.value)} data-testid="select-cap-wan"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">— pilih interface —</option>
              {ifaces.map((i) => <option key={i.name} value={i.name}>{i.name}{i.comment ? ` (${i.comment})` : ""}{i.running ? "" : " — tidak running"}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button type="button" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" disabled={busy || !mbps || Number(mbps) < 1} onClick={save} data-testid="btn-cap-save">{busy ? "Menyimpan…" : "Terapkan ke Router"}</Button>
            {(info?.saved?.mbps || live?.active) && <Button type="button" variant="outline" className="text-rose-400 border-rose-500/40" disabled={busy} onClick={clear} data-testid="btn-cap-clear">Hapus Batas</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
